import { z } from 'zod';

/** Teto do que o editor abre: acima disso a tela trava sem ajudar ninguém. */
export const ARQUIVO_MAXIMO_BYTES = 2 * 1024 * 1024;

export const ItemDaArvoreSchema = z.object({
  nome: z.string(),
  caminho: z.string(),
  tipo: z.enum(['pasta', 'arquivo']),
  tamanho: z.number().int().nonnegative().nullable(),
  /** Arquivo negado aparece na lista, mas cinza e sem conteúdo. */
  negado: z.boolean().default(false),
});
export type ItemDaArvore = z.infer<typeof ItemDaArvoreSchema>;

/**
 * `espiar` abre as raízes de espiada — leitura, e só leitura, fora das raízes
 * de trabalho. Quem decide se pode é a API (papel ADMIN); o daemon decide o
 * que existe para espiar. As proteções que valem para tudo continuam de pé:
 * nome negado, forma suspeita, e escrita nunca.
 */
export const FsArvoreSchema = z.object({
  caminho: z.string().min(1),
  espiar: z.boolean().default(false),
});
export type FsArvore = z.infer<typeof FsArvoreSchema>;

export const FsLerSchema = z.object({
  caminho: z.string().min(1),
  espiar: z.boolean().default(false),
});
export type FsLer = z.infer<typeof FsLerSchema>;

/**
 * A escrita carrega o hash do que a pessoa tinha na tela. Se o arquivo mudou
 * no disco desde então — o Claude editou, ou alguém salvou no VS Code — a
 * gravação é recusada e o conteúdo atual volta, para a tela mostrar os dois
 * lados em vez de sobrescrever calado.
 */
export const FsEscreverSchema = z.object({
  caminho: z.string().min(1),
  conteudo: z.string().max(ARQUIVO_MAXIMO_BYTES),
  hashEsperado: z.string().optional(),
});
export type FsEscrever = z.infer<typeof FsEscreverSchema>;

export const RespostaArvoreSchema = z.discriminatedUnion('ok', [
  z.object({ ok: z.literal(true), caminho: z.string(), itens: z.array(ItemDaArvoreSchema) }),
  z.object({
    ok: z.literal(false),
    erro: z.enum(['FORA_DA_RAIZ', 'NEGADO', 'NAO_ENCONTRADO', 'FALHA']),
    mensagem: z.string().max(300).optional(),
  }),
]);
export type RespostaArvore = z.infer<typeof RespostaArvoreSchema>;

export const RespostaLeituraSchema = z.discriminatedUnion('ok', [
  z.object({
    ok: z.literal(true),
    caminho: z.string(),
    conteudo: z.string(),
    hash: z.string(),
    tamanho: z.number().int().nonnegative(),
    truncado: z.boolean().default(false),
    /** Veio de raiz de espiada: a tela abre sem o botão de salvar. */
    somenteLeitura: z.boolean().default(false),
  }),
  z.object({
    ok: z.literal(false),
    erro: z.enum(['FORA_DA_RAIZ', 'NEGADO', 'NAO_ENCONTRADO', 'GRANDE_DEMAIS', 'BINARIO', 'FALHA']),
    mensagem: z.string().max(300).optional(),
  }),
]);
export type RespostaLeitura = z.infer<typeof RespostaLeituraSchema>;

export const RespostaEscritaSchema = z.discriminatedUnion('ok', [
  z.object({ ok: z.literal(true), hash: z.string(), tamanho: z.number().int().nonnegative() }),
  z.object({
    ok: z.literal(false),
    erro: z.enum(['FORA_DA_RAIZ', 'NEGADO', 'SOMENTE_LEITURA', 'CONFLITO', 'FALHA']),
    mensagem: z.string().max(300).optional(),
    /** No conflito, o que está no disco agora, para a tela comparar. */
    conteudoAtual: z.string().optional(),
    hashAtual: z.string().optional(),
  }),
]);
export type RespostaEscrita = z.infer<typeof RespostaEscritaSchema>;

/** Teto de um pedaço no relay de mídia. Acima disso a mensagem incha o socket. */
export const TRECHO_MAXIMO_BYTES = 512 * 1024;

/**
 * Pedaço de arquivo binário, para o painel tocar mídia que está na máquina.
 *
 * O painel puxa faixa a faixa em vez de a máquina empurrar o arquivo inteiro:
 * assim um vídeo de 200 MB nunca fica inteiro na memória de ninguém, e o
 * `seek` do player vira um pedido de outra faixa em vez de um download novo.
 */
export const FsTrechoSchema = z.object({
  caminho: z.string().min(1),
  inicio: z.number().int().nonnegative(),
  /** Quantos bytes, no máximo. O daemon devolve menos quando o arquivo acaba. */
  tamanho: z.number().int().positive().max(TRECHO_MAXIMO_BYTES),
});
export type FsTrecho = z.infer<typeof FsTrechoSchema>;

/**
 * A chave de um anexo (`anexos/<sessao>/<uuid>.<ext>`, a `CHAVES.anexo` da API) —
 * F6 da revisão de out/2026, ADR-0027, D54. A máquina GUARDA e SERVE anexos só
 * por ela: nunca um caminho do sistema de arquivos viaja no fio, e a pasta
 * guardada fica fora das raízes. Sem `..`, sem barra invertida, sem nada que a
 * expressão não diga.
 */
export const CHAVE_DE_ANEXO = /^anexos\/[A-Za-z0-9_-]{1,64}\/[A-Za-z0-9-]{1,64}\.[A-Za-z0-9]{1,8}$/;
export const ChaveDeAnexoSchema = z.string().regex(CHAVE_DE_ANEXO);

/** `anexo.trecho` (API → daemon): uma faixa do anexo guardado, PELA CHAVE. */
export const AnexoTrechoSchema = z.strictObject({
  chave: ChaveDeAnexoSchema,
  inicio: z.number().int().nonnegative(),
  tamanho: z.number().int().positive().max(TRECHO_MAXIMO_BYTES),
});
export type AnexoTrecho = z.infer<typeof AnexoTrechoSchema>;

/**
 * `agente.anexo_guardado` (daemon → API, com ack): a máquina guardou o anexo e
 * diz o hash que calculou dos bytes. A API confere o tamanho contra o do objeto;
 * só depois disso o cache da VPS começa a contar (D54).
 */
export const AnexoGuardadoSchema = z.strictObject({
  chave: ChaveDeAnexoSchema,
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  tamanho: z.number().int().nonnegative(),
});
export type AnexoGuardado = z.infer<typeof AnexoGuardadoSchema>;

export const RespostaTrechoSchema = z.discriminatedUnion('ok', [
  z.object({
    ok: z.literal(true),
    /** Base64: o socket carrega texto, e mídia não passa pela API por outro caminho. */
    dados: z.string(),
    inicio: z.number().int().nonnegative(),
    lidos: z.number().int().nonnegative(),
    /** Tamanho total do arquivo, que o `Range` do navegador precisa saber. */
    total: z.number().int().nonnegative(),
    mime: z.string(),
  }),
  z.object({
    ok: z.literal(false),
    erro: z.enum(['FORA_DA_RAIZ', 'NEGADO', 'NAO_ENCONTRADO', 'TIPO_RECUSADO', 'FALHA']),
    mensagem: z.string().max(300).optional(),
  }),
]);
export type RespostaTrecho = z.infer<typeof RespostaTrechoSchema>;
